import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../button/Button";
import styles from "./LogInForm.module.scss";
import eye from "../../assets/icons/eye.svg";
import eyeHover from "../../assets/icons/eye-hover.svg";
import checkbox from "../../assets/icons/checkbox.svg";
import eyeSlash from "../../assets/icons/eye-slash.svg";
import eyeSlashHover from "../../assets/icons/eye-slash-hover.svg";
import Input from "../input/Input";
import { useTranslation } from "react-i18next";
import { useLoginMutation } from "../../store/services/auth-api";
import { EVENT_NAMES } from "../../types/event-names";
import { trackEvent } from "../../utils/analytics";

interface LogInFormProps {
  path?: string;
  setFirstLogin?: (val: boolean) => void;
  email?: string; // email already entered on the email-first step
  onChangeEmail?: () => void; // back to the email-first step
}

//need to handle errors in login form
const LogInForm: React.FC<LogInFormProps> = ({
  path = "/home",
  setFirstLogin,
  email,
  onChangeEmail,
}: LogInFormProps) => {
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [emailValue, setEmailValue] = useState(email ?? "");
  const [emailError, setEmailError] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [checked, setChecked] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [login] = useLoginMutation();
  const [isLoader, setIsLoader] = useState(false);

  const isFieldsValid = useCallback(() => {
    return !(
      (emailError || error || emailValue === "" || passwordValue === "") // Add more checks if needed
    );
  }, [emailError, error, emailValue, passwordValue]);

  const handleLogin = useCallback(async () => {
    if (isFieldsValid()) {
      setIsLoader(true);
      try {
        const payload = await login({
          email: emailValue,
          password: passwordValue,
          keepLogin: checked,
        }).unwrap();

        setIsLoader(false);

        if (payload.user.tempPass === "1") {
          setFirstLogin!(true);
        } else {
          if (sessionStorage.firstLogin) {
            trackEvent(EVENT_NAMES.FIRST_LOGIN_SUCCESS);
          }
          navigate("/");
        }
      } catch (error: any) {
        const msg = error.data ? error.data.error : error.status;
        setErrorMsg(msg);
        setError(true);
        setIsLoader(false);
      }
    }
  }, [emailValue, passwordValue, checked, setFirstLogin, navigate, login, isFieldsValid]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        handleLogin();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [emailValue, passwordValue, emailError, error, handleLogin]);

  useEffect(() => {
    if (email) setPasswordFocused(true);
  }, [email]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={styles.root}>
      <div className={styles.root_header}>
        <h1 className={styles.root_header_title}>{t("Login your account")}</h1>
        <h2 className={styles.root_header_subtitle}>
          {t("Enter the email address you used to ")}
          <span className={styles.root_header_subtitle_purchase}>{t("purchase")} </span>
          <span>{t("your Fortect subscription")}</span>
        </h2>
      </div>
      <div className={styles.root_main}>
        {onChangeEmail ? (
          <div className={styles.root_main_email}>
            <span className={styles.root_main_email_value}>{emailValue}</span>
            <button
              type="button"
              className={styles.root_main_email_change}
              onClick={onChangeEmail}
              onKeyDown={(e) => e.stopPropagation()} // Enter here changes the email, it must not also submit the login
            >
              {t("Change")}
            </button>
          </div>
        ) : (
          <div className={styles.root_main_block}>
            <Input
              value={emailValue}
              type="text"
              onFocus={() => setEmailFocused(true)}
              onBlur={() => {
                setEmailFocused(false);
                const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!emailPattern.test(emailValue)) {
                  setEmailError(true);
                } else {
                  setEmailError(false);
                }
              }}
              onChange={(e) => {
                setEmailValue(e.target.value);
                setEmailError(false);
              }}
              focused={emailFocused}
              placeholder={t("Email address")}
              error={emailError}
            />
            {emailError && (
              <p className={styles.root_errorEmail}>{t("Invalid email address. Please enter a valid email.")}</p>
            )}
          </div>
        )}

        <div className={styles.root_main_block}>
          <Input
            type={showPassword ? "text" : "password"}
            value={passwordValue}
            onChange={(e) => {
              setPasswordValue(e.target.value);
              setError(false);
            }}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => {
              setPasswordFocused(false);
            }}
            focused={passwordFocused}
            placeholder={t("Password")}
            error={error}
          />
          <img
            src={showPassword ? eyeSlash : eye}
            alt="eye"
            onMouseOver={(e) => (e.currentTarget.src = showPassword ? eyeSlashHover : eyeHover)}
            onMouseOut={(e) => (e.currentTarget.src = showPassword ? eyeSlash : eye)}
            className={styles.root_main_block_eye}
            onClick={togglePasswordVisibility}
          />
        </div>
      </div>
      {error && <p className={styles.root_error}>{t(errorMsg)}</p>}
      <div className={styles.root_footer}>
        <Button classNames={styles.root_footer_button} onClick={handleLogin} text={t("Log in")} isLoader={isLoader} />

        <div className={styles.root_footer_actions}>
          {/* Keep me signed */}
          <div className={styles.root_footer_actions_keep} onClick={() => setChecked(!checked)}>
            {checked ? (
              <img src={checkbox} alt="checkbox" className={styles.root_footer_actions_keep_img} />
            ) : (
              <div className={styles.root_footer_actions_keep_checkbox} />
            )}

            {t("Keep me signed in")}
          </div>
          <p
            className={styles.root_footer_actions_forgot}
            onClick={() => {
              navigate("/forgot-password", { state: { email: emailValue } });
            }}
          >
            {t("Forgot password?")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LogInForm;
